import type { InputHTMLAttributes } from "react"
import { forwardRef, useEffect, useRef, useState } from "react"

import { ClickAwayListener } from "@mui/base"
import {
  Autocomplete,
  Box,
  Checkbox,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Popper,
  Slider,
  Stack,
  Typography,
  useMediaQuery,
  type BoxProps,
} from "@mui/material"
import { alpha, useTheme } from "@mui/material/styles"
import { isEmpty, noop, uniqBy } from "lodash"
import { Controller, useFormContext, type SubmitHandler } from "react-hook-form"

import { Button, IconButton, SvgIcon, type FC, type iconKeys } from ".."
import { useUserLocation } from "../../local"

type Facet = {
  label: string
  value: string
  experience?: string
}

type SearchFormProps = {
  destinations?: Facet[]
  countries?: Facet[]
  experiences?: Facet[]
  prices?: Facet[]
  durations?: Facet[]
  onSearch?: () => void
  onApplyFilter: (value: any, type: string) => void
  searchParams?: any
}

const SearchForm: FC<SearchFormProps> = ({
  destinations = [],
  countries = [],
  experiences = [],
  durations = [],
  prices = [],
  onSearch,
  onApplyFilter = noop,
  searchParams,
}) => {
  const { control, handleSubmit, getValues } = useFormContext()
  const onSubmit: SubmitHandler<Record<string, unknown>> = () => {
    if (onSearch) onSearch()
  }
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("md"))
  const { currencyFormatter } = useUserLocation()
  const minPrice = 1
  const maxPrice = 10000
  const minDuration = 1
  const maxDuration = 30

  return (
    <Box
      component="form"
      onSubmit={handleSubmit(onSubmit)}
      display={{ xs: "flex", md: "grid" }}
      gridTemplateColumns={{
        md: "repeat(5, minmax(0, 1fr)) auto",
      }}
      rowGap={2}
      sx={(theme) => ({
        "bgcolor": theme.palette["neutral.100"].main,
        "borderRadius": 1.5,
        "position": "relative",

        "& > *:first-of-type": {
          borderRadius: { md: theme.spacing(1.5, 0, 0, 1.5) },

          input: {
            borderRadius: { md: theme.spacing(1.5, 0, 0, 1.5) },
          },
        },

        "& :last-of-type": {
          borderRadius: { md: theme.spacing(0, 1.5, 1.5, 0) },

          input: {
            borderRadius: { md: theme.spacing(0, 1.5, 1.5, 0) },
          },

          svg: {
            borderRadius: 0,
          },
        },

        "& > *:nth-of-type(n+2):nth-of-type(-n+5)": {
          borderLeft: { md: `1px solid ${theme.palette["neutral.200"].main}` },
        },

        [theme.breakpoints.down("md")]: {
          flexDirection: "column",
          height: "100%",
        },
      })}
    >
      <Box>
        <Controller
          name="destination"
          control={control}
          render={({ field: { onChange, value } }) => (
            <Autocomplete
              value={value}
              onChange={(_, newValue) => {
                onChange(newValue)
                onApplyFilter(newValue, "destination")
              }}
              disablePortal={isMobile}
              openOnFocus
              options={destinations}
              renderInput={(params) => (
                <SearchInputField
                  ref={params.InputProps.ref}
                  placeholder="Destinations"
                  aria-label="Destinations"
                  icon="pinMap2"
                  inputProps={params.inputProps}
                />
              )}
              ListboxProps={{
                style: {
                  maxHeight: 324,
                },
                sx: {
                  p: 2,
                },
              }}
              renderOption={(props, option, { selected }) => (
                <ListItem
                  disablePadding
                  selected={selected}
                  sx={(theme) => ({
                    "border": `1px solid transparent`,
                    "minHeight": "48px!important",
                    "py": 3.5,
                    "px": 4,

                    "&.Mui-focused, &.Mui-selected": {
                      bgcolor: `${theme.palette["secondary.200"].main}!important`,
                      borderRadius: 1,
                      color: `${theme.palette["secondary.800"].main}!important`,
                    },
                    "&.Mui-selected": {
                      borderColor: theme.palette["secondary.300"].main,
                    },
                  })}
                  {...props}
                >
                  <ListItemText primaryTypographyProps={{ variant: "link.xs" }}>
                    {option.label}
                  </ListItemText>
                </ListItem>
              )}
              slotProps={{
                popper: {
                  sx: {
                    minWidth: { sm: "276px!important" },
                  },
                },
                paper: {
                  sx: {
                    borderRadius: 1,
                    border: (theme) => `1px solid ${theme.palette["neutral.200"].main}`,
                    boxShadow: (theme) =>
                      `0 0 8px ${alpha(theme.palette["neutral.900"].main, 0.06)}`,
                    fontSize: { xs: 10, md: 12 },
                    my: 1,
                  },
                },
              }}
            />
          )}
        />
      </Box>
      <Box>
        <Controller
          name="country"
          control={control}
          render={({ field: { onChange, value } }) => (
            <Autocomplete
              value={value}
              onChange={(_, newValue) => {
                onChange(newValue)
                onApplyFilter(newValue, "country")
              }}
              disablePortal={isMobile}
              openOnFocus
              options={countries}
              getOptionDisabled={(option) => option.label === "Please select a destination first."}
              renderInput={(params) => (
                <SearchInputField
                  ref={params.InputProps.ref}
                  placeholder="Countries"
                  aria-label="Countries"
                  icon="globe"
                  inputProps={params.inputProps}
                />
              )}
              renderOption={(props, option, { selected }) => (
                <ListItem
                  disablePadding
                  selected={selected}
                  sx={(theme) => ({
                    "border": `1px solid transparent`,
                    "minHeight": "48px!important",
                    "py": 3.5,
                    "px": 4,

                    "&.Mui-focused, &.Mui-selected": {
                      bgcolor: `${theme.palette["secondary.200"].main}!important`,
                      borderRadius: 1,
                      color: `${theme.palette["secondary.800"].main}!important`,
                    },
                    "&.Mui-selected": {
                      borderColor: theme.palette["secondary.300"].main,
                    },
                  })}
                  {...props}
                >
                  <ListItemText primaryTypographyProps={{ variant: "link.xs" }}>
                    {option.label}
                  </ListItemText>
                </ListItem>
              )}
              ListboxProps={{
                style: {
                  maxHeight: 324,
                },
                sx: {
                  p: 2,
                },
              }}
              slotProps={{
                popper: {
                  sx: {
                    minWidth: { sm: "276px!important" },
                  },
                },
                paper: {
                  sx: {
                    borderRadius: 1,
                    border: (theme) => `1px solid ${theme.palette["neutral.200"].main}`,
                    boxShadow: (theme) =>
                      `0 0 8px ${alpha(theme.palette["neutral.900"].main, 0.06)}`,
                    fontSize: { xs: 10, md: 12 },
                    my: 1,
                  },
                },
              }}
            />
          )}
        />
      </Box>
      <Box>
        <Controller
          name="experiences"
          control={control}
          render={({ field: { onChange, value } }) => (
            <Autocomplete
              openOnFocus
              disableCloseOnSelect
              disablePortal={isMobile}
              multiple
              options={experiences}
              value={value}
              onChange={(_, data) => {
                onChange(uniqBy(data, "label"))
                onApplyFilter(data, "experiences")
              }}
              groupBy={(option) => option.experience as string}
              isOptionEqualToValue={(option, value) =>
                option.label === value.label &&
                getValues("experiences").some((x: any) => x.label === option.label)
              }
              renderInput={(params) => (
                <SearchInputField
                  ref={params.InputProps.ref}
                  placeholder={"Experiences"}
                  aria-label="Experiences"
                  icon="palmTree"
                  inputProps={{
                    ...params.inputProps,
                    value: getValues("experiences")
                      .map(({ label }: any) => label)
                      .join(", "),
                  }}
                  disabled
                />
              )}
              renderGroup={({ children, group }) => (
                <Box key={group}>
                  <Box
                    sx={(theme) => ({
                      bgcolor: theme.palette["neutral.100"].main,
                      position: "sticky",
                      px: 4,
                      py: 3.5,
                      top: 0,
                      zIndex: 1,
                    })}
                  >
                    <Box sx={{ variant: "body.sm", fontWeight: 500 }}>{group}</Box>
                  </Box>
                  {children}
                </Box>
              )}
              ListboxProps={{
                style: {
                  maxHeight: 324,
                },
                sx: {
                  "columnGap": 5,
                  "display": "grid",
                  "gridTemplateColumns": { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" },
                  "py": 0,
                  "px": 2,
                  "position": "relative",
                  "overflow": "auto",
                  "width": "100%",

                  "&::before": {
                    content: "''",
                    position: "fixed",
                    top: 16,
                    bottom: 16,
                    display: { xs: "none", md: "block" },
                    width: "1px",
                    bgcolor: (theme) => theme.palette["neutral.200"].main,
                    left: "calc(50% - 5px)",
                    transform: "translateX(-50%)",
                  },
                },
              }}
              renderOption={(props, option, { selected }) => (
                <ListItem
                  disablePadding
                  sx={(theme) => ({
                    "px": 4,
                    "display": "flex",
                    "columnGap": 3,
                    "minHeight": "48px!important",
                    "borderRadius": 1.5,

                    "&[aria-selected='true']": {
                      bgcolor: "transparent!important",
                    },

                    "&.Mui-focused, &.Mui-selected": {
                      bgcolor: `${theme.palette["secondary.200"].main}!important`,
                      borderRadius: 1,
                      color: `${theme.palette["secondary.800"].main}!important`,
                    },
                    "&.Mui-selected": {
                      borderColor: theme.palette["secondary.300"].main,
                    },
                  })}
                  {...props}
                >
                  <ListItemIcon sx={{ minWidth: "unset" }}>
                    <Checkbox
                      icon={
                        <SvgIcon
                          variant="checkboxEmpty"
                          sx={{ fill: (theme) => alpha(theme.palette["neutral.800"].main, 0.12) }}
                        />
                      }
                      checkedIcon={
                        <SvgIcon
                          variant="checkbox"
                          sx={{ fill: (theme) => theme.palette["secondary.800"].main }}
                        />
                      }
                      checked={selected}
                      sx={{ color: (theme) => theme.palette["secondary.200"].main, p: 0 }}
                      disableRipple
                    />
                  </ListItemIcon>
                  <ListItemText primaryTypographyProps={{ variant: "body.sm" }}>
                    {option.label}
                  </ListItemText>
                </ListItem>
              )}
              slotProps={{
                popper: {
                  sx: {
                    width: { md: "600px!important" },
                  },
                },
                paper: {
                  sx: {
                    borderRadius: 1,
                    border: (theme) => `1px solid ${theme.palette["neutral.200"].main}`,
                    boxShadow: (theme) =>
                      `0 0 8px ${alpha(theme.palette["neutral.900"].main, 0.06)}`,
                    fontSize: { xs: 10, md: 12 },
                    my: 1,
                  },
                },
              }}
            />
          )}
        />
      </Box>
      <Box>
        <Controller
          name="budget"
          control={control}
          defaultValue={[minPrice, maxPrice]}
          render={({ field: { onChange, value } }) => (
            <SearchRangeField
              placeholder="Budget"
              label="Price Per Person"
              icon="tag"
              min={minPrice}
              max={maxPrice}
              searchParams={searchParams}
              fieldKey="budget"
              step={10}
              formatValue={currencyFormatter}
              onChange={(newValue) => {
                onChange(newValue)
                onApplyFilter(newValue, "budget")
              }}
            />
          )}
        />
      </Box>
      <Box>
        <Controller
          name="duration"
          control={control}
          defaultValue={[minDuration, maxDuration]}
          render={({ field: { onChange, value } }) => (
            <SearchRangeField
              placeholder="Duration"
              label="Length of Trip"
              icon="calendar"
              min={minDuration}
              max={maxDuration}
              searchParams={searchParams}
              fieldKey="duration"
              step={1}
              formatValue={(x) => `${x} ${x === 1 ? "Day" : "Days"}`}
              onChange={(newValue) => {
                onChange(newValue)
                onApplyFilter(newValue, "duration")
              }}
            />
          )}
        />
      </Box>

      {onSearch && (
        <Stack
          alignItems="center"
          justifyContent="center"
          p={2}
          display={{ xs: "none", md: "flex" }}
        >
          <IconButton
            type="submit"
            aria-label="Search"
            icon={<SvgIcon variant="arrow" size={20} sx={{ transform: "rotate(180deg)" }} />}
            sx={{
              bgcolor: (theme) => theme.palette["secondary.300"].main,
              boxShadow: 0,
              width: { xs: 52 },
              height: { xs: 52 },
              zIndex: 0,
              borderRadius: "100px!important",
            }}
          />
        </Stack>
      )}
      <Box mt="auto" display={{ md: "none" }}>
        <Button
          variant="default"
          type="submit"
          endIcon={<SvgIcon variant="arrow" size={16} sx={{ transform: "rotate(180deg)" }} />}
          sx={{
            width: "100%",
            display: "flex",
            px: { xs: 5 },
            py: { xs: 3.5 },
            justifyContent: "space-between",
          }}
        >
          Search Results
        </Button>
      </Box>
    </Box>
  )
}
export default SearchForm

type InputFieldProps = {
  inputProps: InputHTMLAttributes<HTMLInputElement>
  icon: string
} & BoxProps<"input">

const SearchInputField = forwardRef<any, InputFieldProps>(({ inputProps, icon, ...props }, ref) => (
  <Box ref={ref} position="relative" sx={{ isolation: "isolate" }}>
    <Box
      component="input"
      type="text"
      sx={(theme) => ({
        "bgcolor": "transparent",
        "border": { xs: `1px solid ${theme.palette["neutral.200"].main}`, md: "0" },
        "borderRadius": { xs: 1.5, md: 0 },
        "color": theme.palette["neutral.700"].main,
        "display": "block",
        "fontWeight": 500,
        "height": 68,
        "fontSize": { xs: "16px", md: "12px" },
        "p": 4,
        "pl": 11.5,
        "width": "100%",
        "&::placeholder": {
          color: theme.palette["neutral.700"].main,
          fontWeight: 400,
          opacity: 0.7,
          fontSize: { xs: "16px", md: "12px" },
        },

        "&:focus": {
          "bgcolor": theme.palette["secondary.200"].main,
          "color": theme.palette["secondary.800"].main,
          "outline": "none",
          "fontSize": { xs: "16px", md: "12px" },

          "&::placeholder": {
            color: theme.palette["secondary.800"].main,
            fontSize: { xs: "16px", md: "12px" },
          },

          "& + svg": {
            color: theme.palette["secondary.800"].main,
            fontSize: { xs: "16px", md: "12px" },
          },
        },
      })}
      {...props}
      {...inputProps}
    />
    <SvgIcon
      variant={icon as iconKeys}
      size={16}
      sx={{
        position: "absolute",
        left: 20,
        top: "50%",
        transform: "translateY(-50%)",
        zIndex: 1,
      }}
    />
  </Box>
))
SearchInputField.displayName = "SearchInputField"

type RangeFieldProps = {
  min?: number
  max?: number
  step?: number
  searchParams?: any
  fieldKey: string
  formatValue?: (value: number) => string
  placeholder: string
  label: string
  icon: string
  onChange: (value: [number, number]) => void
}

const SearchRangeField: FC<RangeFieldProps> = ({
  min = null,
  max = null,
  searchParams,
  fieldKey,
  step = 1,
  placeholder,
  label,
  icon,
  formatValue = (x) => `${x}`,
  onChange = noop,
}) => {
  const [open, setOpen] = useState(false)
  const anchorRef = useRef<HTMLDivElement>(null)
  const [value, setValue] = useState<[number | null, number | null] | null>([null, null])
  const [limits, setLimits] = useState<[number, number]>([0, 0])
  const [isDirty, setIsDirty] = useState(false)
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("md"))

  const handleToggle = () => {
    setOpen((previousOpen) => !previousOpen)
  }

  const handleClose = (event: Event | React.SyntheticEvent) => {
    if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
      return
    }

    setOpen(false)
  }

  const handleChange = (_: Event, newValue: number | number[]) => {
    setValue(newValue as [number, number])
    setIsDirty(true)
  }

  // Apply local state values when input min/max props change.
  useEffect(() => {
    if (min !== null && max !== null) {
      const newLimits = [min, max] as [number, number]
      setLimits(newLimits)
      // Init default select values if not set currently.
      if (value?.[0] === null || value?.[1] === null) {
        setValue([min, max])
      }
    }
  }, [min, max])

  const fieldState = searchParams?.[fieldKey]
  const isFieldStateEmpty = !fieldState?.min && !fieldState?.max
  useEffect(() => {
    if (isEmpty(fieldState) || isFieldStateEmpty) {
      if (value?.[0] !== null && value?.[1] !== null) {
        setIsDirty(false)
        setValue([min || 0, max || 0])
        setLimits([min || 0, max || 0])
      }
    } else {
      setIsDirty(true)
      setValue([fieldState?.min || 0, fieldState?.max || 0])
    }
  }, [fieldState])

  return (
    <>
      <Box
        ref={anchorRef}
        height="100%"
        position="relative"
        tabIndex={0}
        onFocus={handleToggle}
        // onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={(theme) => ({
          "isolation": "isolate",
          "&:focus-within .range-field": {
            "bgcolor": theme.palette["secondary.200"].main,
            "color": theme.palette["secondary.800"].main,
            "outline": "none",

            "& + svg": {
              color: theme.palette["secondary.800"].main,
            },
          },
        })}
      >
        <Stack
          className="range-field"
          component="div"
          sx={(theme) => ({
            bgcolor: theme.palette["neutral.100"].main,
            color: theme.palette["neutral.700"].main,
            border: { xs: `1px solid ${theme.palette["neutral.200"].main}`, md: "0" },
            borderRadius: { xs: 1.5, md: 0 },
            height: 68,
            justifyContent: "center",
            p: 4,
            pl: 11.5,
            width: "100%",
          })}
        >
          {!isDirty && (
            <Typography
              variant="body.sm"
              sx={{
                opacity: 0.7,
                fontSize: { xs: "16px", md: "12px" },
              }}
            >
              {placeholder}
            </Typography>
          )}
          {isDirty && (
            <Typography
              variant="body.sm"
              fontWeight={500}
              sx={{ fontSize: { xs: "16px", md: "12px" } }}
            >
              {placeholder === "Duration"
                ? `${value?.[0] || 0} - ${value?.[1] || 0} Days`
                : `${formatValue(value?.[0] || 0)} - ${formatValue(value?.[1] || 0)}`}
            </Typography>
          )}
        </Stack>
        <SvgIcon
          variant={icon as iconKeys}
          size={16}
          sx={{
            position: "absolute",
            left: 20,
            top: "50%",
            transform: "translateY(-50%)",
            zIndex: 1,
          }}
        />
      </Box>

      <Popper
        open={open}
        anchorEl={anchorRef.current}
        placement="bottom"
        disablePortal={isMobile}
        modifiers={[
          {
            name: "flip",
            enabled: true,
            options: {
              altBoundary: true,
              rootBoundary: "viewport",
              padding: 8,
            },
          },
          {
            name: "preventOverflow",
            enabled: true,
            options: {
              altAxis: true,
              altBoundary: true,
              tether: true,
              rootBoundary: "viewport",
              padding: 8,
            },
          },
          {
            name: "arrow",
            enabled: false,
          },
        ]}
        sx={{
          width: { xs: "100%", md: 324 },
          zIndex: 10,
        }}
      >
        <Box
          bgcolor="white"
          my={1}
          borderRadius={1}
          boxShadow={(theme) => `0 0 8px ${alpha(theme.palette["neutral.900"].main, 0.06)}`}
        >
          <ClickAwayListener onClickAway={handleClose}>
            <Box px={5} py={2}>
              <Typography variant="body.sm" fontWeight={500}>
                {label}
              </Typography>
              <Box px={1.5}>
                <Slider
                  value={[value?.[0] || 0, value?.[1] || 0]}
                  onChange={handleChange}
                  onChangeCommitted={(_, value) => onChange(value as [number, number])}
                  step={step}
                  min={limits[0] || 0}
                  max={limits[1] || 0}
                  sx={(theme) => ({
                    "height": 8,
                    "p": 0,

                    "& .MuiSlider-rail": {
                      bgcolor: theme.palette["neutral.200"].main,
                    },

                    "& .MuiSlider-track": {
                      bgcolor: theme.palette["secondary.300"].main,
                      border: 0,
                      opacity: 1,
                    },

                    "& .MuiSlider-thumb": {
                      "bgcolor": theme.palette["neutral.100"].main,
                      "border": `1px solid ${theme.palette["secondary.800"].main}`,
                      "boxShadow": 0,
                      "height": 15,
                      "width": 15,

                      "&::before": {
                        boxShadow: 0,
                      },

                      "&:focus, &:hover, &.Mui-active, &.Mui-focusVisible": {
                        boxShadow: "inherit",
                      },
                    },
                  })}
                />
              </Box>
              <Stack direction="row" justifyContent="space-between" pt={3.5} pb={1.5}>
                <Typography variant="body.xs">{`${formatValue(value?.[0] || 0)}`}</Typography>
                <Typography variant="body.xs">{`${formatValue(value?.[1] || 0)}`}</Typography>
              </Stack>
            </Box>
          </ClickAwayListener>
        </Box>
      </Popper>
    </>
  )
}
